import { useState } from 'react'
import { Box, Button, IconButton, Stack, Typography } from '@mui/material'
import { Check as CheckIcon, Edit as EditIcon } from '@mui/icons-material'
import { FormInput } from '../ui'
import { SideCard } from './SideCard'
import { InfoRow } from './DetailViewWrappers'
import { LocationIcon, IndustryIcon } from './CustomIcons'
import { themeColors } from '../../app/layout/theme'

export interface AddressAndTaxInfoCardProps {
  address: string | null
  gst: string | null
  pincode: string | null
  onSave: (data: { address: string | null; gst: string | null; pincode: string | null }) => void
  isSaving: boolean
  canEdit: boolean
}

export function AddressAndTaxInfoCard({
  address,
  gst,
  pincode,
  onSave,
  isSaving,
  canEdit,
}: AddressAndTaxInfoCardProps) {
  const [editing, setEditing] = useState(false)
  const [vals, setVals] = useState({ address: '', gst: '', pincode: '' })
  const [errors, setErrors] = useState<{ gst?: string; pincode?: string }>({})

  const handleEditClick = () => {
    setVals({ address: address || '', gst: gst || '', pincode: pincode || '' })
    setErrors({})
    setEditing(true)
  }

  const validate = () => {
    const errs: { gst?: string; pincode?: string } = {}
    if (vals.gst && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i.test(vals.gst)) {
      errs.gst = 'Invalid GST format'
    }
    if (vals.pincode && !/^[1-9][0-9]{5}$/.test(vals.pincode)) {
      errs.pincode = 'Invalid Pincode (6 digits)'
    }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSave = () => {
    if (!validate()) return
    onSave({
      address: vals.address.trim() || null,
      gst: vals.gst.trim().toUpperCase() || null,
      pincode: vals.pincode.trim() || null,
    })
    setEditing(false)
  }

  const isDirty = vals.address !== (address || '') || vals.gst !== (gst || '') || vals.pincode !== (pincode || '')

  return (
    <SideCard
      title="Address & Tax Info"
      action={
        canEdit && !editing ? (
          <IconButton
            size="small"
            onClick={handleEditClick}
            sx={{
              color: '#9ca3af',
              p: 0.25,
              '&:hover': {
                color: themeColors.primary,
                bgcolor: 'rgba(99, 102, 241, 0.08)',
              },
            }}
          >
            <EditIcon sx={{ fontSize: 14 }} />
          </IconButton>
        ) : undefined
      }
    >
      {editing ? (
        <Box>
          <FormInput
            label="Address"
            value={vals.address}
            onChange={(e) => setVals((prev) => ({ ...prev, address: e.target.value }))}
            fullWidth
            sx={{ mb: 1.5 }}
          />
          <FormInput
            label="GST"
            fieldType="gstin"
            value={vals.gst}
            onChange={(e) => {
              const val = e.target.value
              setVals((prev) => ({ ...prev, gst: val }))
              const isInvalid = val && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i.test(val)
              setErrors((prev) => ({ ...prev, gst: isInvalid ? 'Invalid GST format' : '' }))
            }}
            error={!!errors.gst}
            helperText={errors.gst}
            fullWidth
            sx={{ mb: 1.5 }}
          />
          <FormInput
            label="Pincode"
            fieldType="pincode"
            value={vals.pincode}
            onChange={(e) => {
              const val = e.target.value
              setVals((prev) => ({ ...prev, pincode: val }))
              const isInvalid = val && !/^[1-9][0-9]{5}$/.test(val)
              setErrors((prev) => ({ ...prev, pincode: isInvalid ? 'Invalid Pincode (6 digits)' : '' }))
            }}
            error={!!errors.pincode}
            helperText={errors.pincode}
            fullWidth
            sx={{ mb: 1.5 }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isDirty && (
              <Button
                size="small"
                variant="contained"
                startIcon={<CheckIcon />}
                disabled={isSaving || !!errors.gst || !!errors.pincode}
                onClick={handleSave}
                sx={{ borderRadius: '6px' }}
              >
                Save
              </Button>
            )}
            <Button size="small" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </Box>
        </Box>
      ) : (
        <Stack spacing={0.5}>
          <InfoRow
            icon={<LocationIcon sx={{ fontSize: 16 }} />}
            label="Address"
            value={<Typography fontSize="12.5px">{address || '—'}</Typography>}
          />
          <InfoRow
            icon={<IndustryIcon sx={{ fontSize: 16 }} />}
            label="GST"
            value={<Typography fontSize="12.5px">{gst || '—'}</Typography>}
          />
          <InfoRow
            icon={<LocationIcon sx={{ fontSize: 16 }} />}
            label="Pincode"
            value={<Typography fontSize="12.5px">{pincode || '—'}</Typography>}
          />
        </Stack>
      )}
    </SideCard>
  )
}
